/**
 * CareDiscussionRoomNewCarer — bespoke carer-side room creation form.
 *
 * Distinct from the family form (`CareDiscussionRoomNew.jsx`): the
 * carer never types their own email here (the backend derives it from
 * the authenticated session). Fields ask for the family contact
 * instead, and the copy makes the direction of invitation clear.
 *
 * Route: /care-discussion-room/new-carer
 * Backend: POST /api/care-rooms with creator_role="carer"
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError as formatErr } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";

export default function CareDiscussionRoomNewCarer() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    family_name: "",
    family_email: "",
    client_label: "",
    proposed_start_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form, creator_role: "carer" };
      // client_label is required by the backend schema — fall back to a
      // short label built from the family contact's name if the carer
      // left the (optional) placement reference blank.
      if (!payload.client_label) {
        payload.client_label = (payload.family_name || payload.family_email || "New room").slice(0, 60);
      }
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/api/care-rooms", payload);
      toast.success("Care Discussion Room created");
      nav(`/care-discussion-room/${data.room_id}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-cdr-new-carer">
      <PageHero
        eyebrow="Care Discussion Room"
        title="Create a Care Discussion Room"
        description="You'll be added to the room as the carer. Enter the family contact you want to invite."
      />
      <section className="section pt-12">
        <div className="container-jcc max-w-2xl">
          <form onSubmit={submit} className="card-base p-7 md:p-9 space-y-5" data-testid="cdr-new-carer-form">
            <p className="text-sm text-ink-muted" data-testid="cdr-new-carer-explainer">
              Enter the details of the family member or representative you would
              like to invite. You will automatically be added to the room as the
              carer.
            </p>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                Family contact name <span className="text-red-500">*</span>
              </label>
              <input
                required
                name="family_name"
                value={form.family_name}
                onChange={onChange}
                placeholder="e.g. Jane Smith"
                data-testid="cdr-family-name"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                Family contact email <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="email"
                name="family_email"
                value={form.family_email}
                onChange={onChange}
                placeholder="jane@example.com"
                data-testid="cdr-family-email"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
              />
              <p className="mt-1 text-xs text-ink-soft">
                They&rsquo;ll sign in with this email using a one-time link &mdash; no password needed.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                Person needing care &mdash; name or preferred reference (optional)
              </label>
              <input
                name="client_label"
                value={form.client_label}
                onChange={onChange}
                placeholder="e.g. Mrs J.S. — or leave blank"
                data-testid="cdr-client-label"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
              />
              <p className="mt-1 text-xs text-ink-soft">
                Use initials or a short label &mdash; full name is not required.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                Proposed start date (optional)
              </label>
              <input
                type="date"
                name="proposed_start_date"
                value={form.proposed_start_date}
                onChange={onChange}
                data-testid="cdr-start-date"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">
                Brief introductory message (optional)
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={onChange}
                rows={4}
                maxLength={2000}
                data-testid="cdr-notes"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-teal focus:ring-1 focus:ring-teal outline-none"
              />
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="btn-teal"
                data-testid="cdr-submit"
              >
                {submitting ? "Creating…" : "Create Care Discussion Room"}
              </button>
              <button
                type="button"
                onClick={() => nav(-1)}
                className="btn-outline"
              >
                <ArrowLeft className="w-4 h-4" /> Cancel
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
